import { Link, useLocation } from 'react-router'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { LastActivityCell } from '../list/LastActivityCell'
import { LastContactCell } from '../list/LastContactCell'
import type { OrganisationRow } from '../query/types'

/**
 * The phone presentation of the organisation list: name, domain or city, and one moment that
 * follows the sort (the last activity when the list is sorted on it, else the last contact),
 * as the contact cards do. The search, view, filter, sort and page of the list travel with the
 * link as the way back (`OrganisationPage` reads them), as they do from the table.
 *
 * The name is a stretched link: the whole card is the tap target (≥ 44 px, as the dossier cards).
 */
export function OrganisationCardList({ rows, sort }: { rows: OrganisationRow[]; sort: string }) {
  const location = useLocation()
  const byActivity = sort.replace(/^-/, '') === 'last_activity_at'
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((organisation) => (
        <li key={organisation.id} className="vonk-card relative flex items-start gap-3 p-3">
          <EntityAvatar kind="company" name={organisation.name} size="md" />
          {/* `flex-1`: the last-activity line is a size container and adds no width; without it the text is only as wide as the name. */}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Link
              to={`/relaties/organisaties/${organisation.id}`}
              state={{ from: location.search }}
              className="font-medium text-foreground underline-offset-2 after:absolute after:inset-0 after:content-[''] hover:underline"
            >
              {organisation.name}
            </Link>
            <p className="truncate text-sm text-muted-foreground">{organisation.domains[0] ?? organisation.city ?? '—'}</p>
            <div className="text-sm">
              {byActivity ? (
                <LastActivityCell activity={organisation.last_activity} />
              ) : (
                <LastContactCell lastContactAt={organisation.last_contact_at} />
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}
